import React, { createContext, useContext, useMemo } from "react";
import { useFeatureFlags } from "../components/FeatureFlag";
import { translations, Language } from "./translations";

type TranslateFn = (
  key: string,
  vars?: Record<string, string | number>,
) => string;

type LanguageContextValue = {
  language: Language;
  t: TranslateFn;
};

const LanguageContext = createContext<LanguageContextValue>({
  language: "en",
  t: (key: string) => key,
});

function interpolate(text: string, vars?: Record<string, string | number>) {
  if (!vars) {
    return text;
  }
  return Object.entries(vars).reduce((acc, [key, value]) => {
    return acc.replaceAll(`{${key}}`, String(value));
  }, text);
}

export function LanguageProvider({ children }: React.PropsWithChildren) {
  const { flagList } = useFeatureFlags(["language.indonesian"]);
  const language: Language = flagList[0] ? "id" : "en";
  const t = useMemo<TranslateFn>(() => {
    return (key: string, vars?: Record<string, string | number>) => {
      const translated = translations[language]?.[key] || key;
      return interpolate(translated, vars);
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT() {
  return useContext(LanguageContext).t;
}

export function useLanguage() {
  return useContext(LanguageContext).language;
}
